import type { RecorderStatus } from "./recorder-session";
import { persistence } from "./recording-persistence";

/**
 * Whether leaving the page right now would cost the author something they cannot get back.
 *
 * `failed` is the state that depends on the second argument, and that is the whole point of having
 * one. A take waiting behind the Retry button is normally also on disk, and prompting about it on
 * every reload is a nag — but persistence is best effort, and where it stored nothing the blob in
 * memory is the only copy of the recording that exists.
 *
 * @param status - The recorder's current state.
 * @param hasDurableCopy - Whether a playable copy of the current take is on disk.
 * @returns True while the page holds something a reload would cut short.
 */
export function shouldGuardUnload(status: RecorderStatus, hasDurableCopy = false) {
	return (
		status === "recording" ||
		status === "paused" ||
		status === "assembling" ||
		status === "uploading" ||
		(status === "failed" && !hasDurableCopy)
	);
}

/** The slice of the recorder store this guard reads. Passed in, so the store does not import back. */
interface StatusStore {
	getState: () => { status: RecorderStatus };
	subscribe: (listener: (state: { status: RecorderStatus }) => void) => () => void;
}

/**
 * Ask the browser to confirm before the page goes, while a recording is running or shipping.
 *
 * Courtesy, not protection, and worth stating plainly: Chrome ignores the prompt entirely unless
 * the author has interacted with the page, the message is never shown — every browser substitutes
 * its own — and a closed tab still ends the capture. What it buys is the accidental reload.
 *
 * @param store - The recorder store to follow.
 */
export function installUnloadGuard(store: StatusStore) {
	if (typeof window === "undefined") return;

	let isInstalled = false;
	const onBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();

	function sync(status: RecorderStatus) {
		const isWanted = shouldGuardUnload(status, persistence.hasDurableRecord());
		if (isWanted === isInstalled) return;
		isInstalled = isWanted;
		if (isWanted) window.addEventListener("beforeunload", onBeforeUnload);
		else window.removeEventListener("beforeunload", onBeforeUnload);
	}

	sync(store.getState().status);
	store.subscribe((state) => sync(state.status));
}
